import { UploaderConstants } from 'src/constants/ProjectConstants';

/**
 * Calculates the total size of an array of files
 * @param files Array of File objects
 * @returns Total size in bytes
 */
export function calculateFilesTotalSize(files: File[]): number {
  return files.reduce((total, file) => total + file.size, 0);
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  error?: {
    messageId: string;
    messageParams: Record<string, any>;
  };
}

/**
 * Performs comprehensive validation for file attachments by checking both count and size limits.
 * This function validates whether the total attempted files would exceed
 * the configured maximum number of attachments or total size limits.
 *
 * @param allAttemptedFiles Array of File objects representing all files user has attempted to attach
 * @param maxNumberOfAttachments Optional maximum number of attachments allowed
 * @param maxTotalAttachmentsSize Optional maximum total size allowed in bytes
 * @returns ValidationResult object containing:
 *          - isValid: true if validation passes, false if any limit is exceeded
 *          - error: error details when validation fails (undefined when valid)
 */
export function validateFileAttachments(
  allAttemptedFiles: File[],
  maxNumberOfAttachments?: number,
  maxTotalAttachmentsSize?: number,
): ValidationResult {
  const totalAttemptedFilesCount = allAttemptedFiles.length;
  const totalAttemptedFilesSize = calculateFilesTotalSize(allAttemptedFiles);

  // Validate attachment count - check if total attempted files exceed limit
  if (maxNumberOfAttachments && maxNumberOfAttachments > 0) {
    if (totalAttemptedFilesCount > maxNumberOfAttachments) {
      return {
        isValid: false,
        error: {
          messageId: UploaderConstants.ERROR_MAX_ATTACHMENTS_EXCEEDED,
          messageParams: {
            maxLimit: maxNumberOfAttachments,
            attemptingToAdd: totalAttemptedFilesCount,
          },
        },
      };
    }
  }

  // Validate total size - check if total attempted files size exceeds limit
  if (maxTotalAttachmentsSize && maxTotalAttachmentsSize > 0) {
    if (totalAttemptedFilesSize > maxTotalAttachmentsSize) {
      const maxSizeMB = (maxTotalAttachmentsSize / (1024 * 1024)).toFixed(2);
      const currentSizeMB = (totalAttemptedFilesSize / (1024 * 1024)).toFixed(
        2,
      );

      return {
        isValid: false,
        error: {
          messageId: UploaderConstants.ERROR_TOTAL_SIZE_EXCEEDED,
          messageParams: { maxSizeMB: maxSizeMB, currentSizeMB: currentSizeMB },
        },
      };
    }
  }

  return { isValid: true };
}
